import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { keys } from "../../lib/queryClient";

/**
 * Live updates for the group.
 *
 * One channel, four tables. Realtime honours RLS, and every member can read
 * check_ins and goals, so this broadcasts to the four of us and nobody else.
 *
 * The handler only invalidates — it never merges rows into the cache by hand.
 * Streaks are computed server-side, so a refetch is the only way to get them
 * right, and with four people the extra request costs nothing.
 */
export function useRealtime(enabled: boolean) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    const channel = supabase
      .channel("lock-in-group")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "check_ins" },
        () => {
          void queryClient.invalidateQueries({ queryKey: keys.feed });
          void queryClient.invalidateQueries({ queryKey: keys.goals });
          void queryClient.invalidateQueries({ queryKey: keys.recap });
          void queryClient.invalidateQueries({ queryKey: keys.checkIns });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "goals" },
        () => {
          void queryClient.invalidateQueries({ queryKey: keys.goals });
          void queryClient.invalidateQueries({ queryKey: keys.feed });
          void queryClient.invalidateQueries({ queryKey: keys.recap });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "check_in_likes" },
        () => {
          void queryClient.invalidateQueries({ queryKey: keys.likes });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "check_in_comments" },
        () => {
          void queryClient.invalidateQueries({ queryKey: keys.comments });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled, queryClient]);
}
